import { CloseIcon, DragHandleIcon } from '@chakra-ui/icons'
import { Box, Button, CloseButton, Flex, IconButton, Text, VStack } from '@chakra-ui/react'
import {
  FieldMap,
  FieldTypes,
  ICD10Admin,
  ListField,
  objectToArray,
  TextAreaField,
  TextField,
  WithId,
} from '@hb/shared'
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
} from '@hello-pangea/dnd'
import { useCallback, useState } from 'react'
import { AddItem } from '../../../AddItem'
import { ActionButton, DeleteButton, SolidActionButton } from '../../../Buttons'
import { GenericEditModal } from '../../../DataView/GenericEditModal'
import { Editable } from '../../../forms/Input'
import { ScrollableBodyContainer } from '../../../Layout/ScrollableBodyContainer'
import { CollapseHorizontal } from '../../../shared'
import type { ICD10GroupWithId } from './context'
import { ICD10LibraryProvider, useICD10LibraryContext } from './context'
import { TestFeatureModal } from './TestFeatureModal'

// ─── Field definitions ────────────────────────────────────────────────────────

const codeTextField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'ICD-10 Code (e.g. O09.0)',
}

const descriptionTextField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Description (provider-visible)',
}

const keywordItemField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Keyword',
}

const keywordsListField: ListField = {
  name: 'Keywords',
  itemFields: keywordItemField,
  itemName: 'Keyword',
}

const autoTriggerTextField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Auto-trigger condition',
  optional: true,
}

const addCodeField: FieldMap = {
  name: 'Add ICD-10 Code',
  children: {
    code: codeTextField,
    description: descriptionTextField,
    keywords: keywordsListField,
    autoTrigger: autoTriggerTextField,
  },
}

const groupNameTextField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Category Name',
}

// ─── Keyword chip ─────────────────────────────────────────────────────────────

const CodeKeywordChip = ({
  keyword,
  onDelete,
}: {
  keyword: string
  onDelete: () => Promise<void>
}) => {
  const [hovered, setHovered] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const handleDelete = useCallback(async () => {
    setDeleteLoading(true)
    try {
      await onDelete()
    } finally {
      setDeleteLoading(false)
    }
  }, [onDelete])
  return (
    <Flex
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      py={0.5}
      px={2}
      cursor="pointer"
      bg="green.100"
      _dark={{ bg: 'green.800' }}
      borderRadius={4}
      align="center">
      <Text fontSize="sm" color="green.800" _dark={{ color: 'green.100' }} fontWeight={500}>
        {keyword}
      </Text>
      <CollapseHorizontal in={hovered || deleteLoading} width={24}>
        <Box pl={1}>
          <IconButton
            aria-label={`Remove ${keyword}`}
            icon={<CloseIcon boxSize={2} />}
            size="xs"
            variant="ghost"
            isLoading={deleteLoading}
            onClick={handleDelete}
          />
        </Box>
      </CollapseHorizontal>
    </Flex>
  )
}

// ─── Code keywords sub-component ──────────────────────────────────────────────

const ICD10CodeKeywords = ({ groupId, code }: { groupId: string; code: WithId<ICD10Admin> }) => {
  const { handleUpdateCodeKeywords } = useICD10LibraryContext()

  const handleDeleteKeyword = useCallback(
    async (keyword: string) => {
      const next = code.keywords.filter(k => k !== keyword)
      const result = await handleUpdateCodeKeywords(groupId, code.code, next)
      if (result.error) throw new Error(result.error)
    },
    [handleUpdateCodeKeywords, groupId, code.code, code.keywords],
  )

  const handleAddKeyword = useCallback(
    async (keyword: string) => {
      if (code.keywords.map(k => k.toLowerCase()).includes(keyword.toLowerCase())) {
        return { error: 'Keyword already exists' }
      }
      return handleUpdateCodeKeywords(groupId, code.code, [...code.keywords, keyword])
    },
    [handleUpdateCodeKeywords, groupId, code.code, code.keywords],
  )

  return (
    <Flex align="center" gap={2} flexWrap="wrap">
      <Text fontSize="xs" color="gray.700" minW="72px" pt={0.5}>
        Keywords
      </Text>
      <Flex flexWrap="wrap" gap={1.5} flex={1} minWidth={0}>
        {code.keywords.map(kw => (
          <CodeKeywordChip key={kw} keyword={kw} onDelete={() => handleDeleteKeyword(kw)} />
        ))}
      </Flex>
      <AddItem placeholder="Keyword" itemName="Keyword" onAdd={handleAddKeyword} />
    </Flex>
  )
}

// ─── Single code row ──────────────────────────────────────────────────────────

const ICD10CodeRow = ({
  groupId,
  code,
  dragHandleProps,
}: {
  groupId: string
  code: WithId<ICD10Admin>
  dragHandleProps?: DraggableProvidedDragHandleProps | null
}) => {
  const { handleUpdateCodeDescription, handleUpdateCodeAutoTrigger, handleDeleteCode } =
    useICD10LibraryContext()

  return (
    <Flex
      w="100%"
      direction="column"
      borderRadius={4}
      bg="white"
      _dark={{ bg: 'gray.800' }}
      shadow="sm">
      <Flex
        borderColor="gray.300"
        borderBottomWidth={1}
        py={2}
        px={3}
        _dark={{ borderColor: 'gray.700' }}
        align="center"
        gap={3}>
        {dragHandleProps ? (
          <IconButton
            aria-label="Reorder code"
            boxShadow="1px 1px 4px #00000033"
            bg="white"
            size="xs"
            icon={<DragHandleIcon w={2.5} />}
            {...dragHandleProps}
          />
        ) : null}
        <Text fontWeight={700} color="gray.800" _dark={{ color: 'gray.200' }} flex={1}>
          {code.code}
        </Text>
        <DeleteButton itemName={code.code} onDelete={() => handleDeleteCode(groupId, code.code)} />
      </Flex>
      <Flex gap={1} flexDirection="column" py={2} px={3}>
        <Flex align="center" gap={2}>
          <Text fontSize="xs" color="gray.700" fontWeight={600} minW="72px">
            Description
          </Text>
          <Editable
            field={descriptionTextField}
            value={code.description}
            onSubmit={v => handleUpdateCodeDescription(groupId, code.code, v)}
            dataCellProps={{ fontSize: 'sm', py: 0 }}
          />
        </Flex>

        <ICD10CodeKeywords groupId={groupId} code={code} />

        <Flex align="center" gap={2}>
          <Text fontSize="xs" color="gray.700" minW="72px">
            Auto-trigger
          </Text>
          <Editable
            field={autoTriggerTextField}
            value={code.autoTrigger ?? ''}
            onSubmit={v => handleUpdateCodeAutoTrigger(groupId, code.code, v)}
            dataCellProps={{
              fontSize: 'sm',
              py: 0,
              color: code.autoTrigger ? undefined : 'gray.400',
            }}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

// ─── Group list card ──────────────────────────────────────────────────────────

const ICD10GroupCard = ({
  group,
  groupDragHandleProps,
  onOpen,
}: {
  group: ICD10GroupWithId
  groupDragHandleProps?: DraggableProvidedDragHandleProps | null
  onOpen: (id: string) => void
}) => {
  const { handleUpdateGroupName, handleDeleteGroup } = useICD10LibraryContext()

  const codeCount = objectToArray(group.codes).length

  return (
    <Flex
      w="100%"
      direction="column"
      px={4}
      py={2}
      borderRadius={4}
      shadow="md"
      bg="gray.50"
      _dark={{ bg: 'gray.900' }}>
      <Flex align="center" gap={3}>
        {groupDragHandleProps ? (
          <IconButton
            aria-label="Reorder category"
            boxShadow="1px 1px 4px #00000044"
            bg="white"
            size="xs"
            icon={<DragHandleIcon w={3} />}
            {...groupDragHandleProps}
          />
        ) : null}
        <Flex flex={1} minWidth={0} pt={0.5} align="center">
          <Editable
            field={groupNameTextField}
            value={group.name}
            onSubmit={v => handleUpdateGroupName(group.id, v)}
            dataCellProps={{ fontSize: 'xl', fontWeight: 500 }}
          />
        </Flex>
        <ActionButton size="sm" variant="outline" onClick={() => onOpen(group.id)}>
          {`${codeCount} codes`}
        </ActionButton>
        <DeleteButton itemName="Category" onDelete={() => handleDeleteGroup(group.id)} />
      </Flex>
    </Flex>
  )
}

// ─── Group detail view ────────────────────────────────────────────────────────

const ICD10GroupDetailView = ({
  group,
  onBack,
}: {
  group: ICD10GroupWithId
  onBack: () => void
}) => {
  const { handleAddCode, handleCodeDragEnd } = useICD10LibraryContext()
  const [addOpen, setAddOpen] = useState(false)

  const sortedCodes: WithId<ICD10Admin>[] = objectToArray(group.codes).sort(
    (a, b) => a.rank - b.rank,
  )

  const onCodeDragEnd = handleCodeDragEnd(group.id)

  return (
    <>
      <ScrollableBodyContainer
        header={
          <Flex
            align="center"
            w="100%"
            px={3}
            py={2}
            gap={1.5}
            bg="gray.50"
            _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
            borderBottomWidth={1}>
            <Text
              fontSize="xl"
              fontWeight={600}
              color="gray.700"
              _dark={{ color: 'gray.200' }}
              flex={1}>
              {group.name}
            </Text>
            <SolidActionButton
              size="sm"
              mr={2}
              onClick={e => {
                e.stopPropagation()
                setAddOpen(true)
              }}>
              + Add Code
            </SolidActionButton>
            <CloseButton
              bg="blackAlpha.50"
              _hover={{ bg: 'blackAlpha.100' }}
              _dark={{ bg: 'whiteAlpha.50', _hover: { bg: 'whiteAlpha.100' } }}
              onClick={onBack}
            />
          </Flex>
        }
        flexProps={{ bg: 'gray.100', _dark: { bg: 'gray.700' } }}
        body={
          <DragDropContext onDragEnd={onCodeDragEnd}>
            <Droppable droppableId={`codes-${group.id}`}>
              {provided => (
                <VStack
                  p={2}
                  spacing={2}
                  w="100%"
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {sortedCodes.map((code, index) => (
                    <Draggable
                      key={code.code}
                      draggableId={`${group.id}--${code.code}`}
                      index={index}>
                      {(draggableProvided, draggableSnapshot) => (
                        <Box
                          w="100%"
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          boxShadow={draggableSnapshot.isDragging ? 'lg' : undefined}>
                          <ICD10CodeRow
                            groupId={group.id}
                            code={code}
                            dragHandleProps={draggableProvided.dragHandleProps}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </VStack>
              )}
            </Droppable>
          </DragDropContext>
        }
      />
      <GenericEditModal<ICD10Admin>
        field={addCodeField}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(data, _onProgress) => handleAddCode(group.id, data)}
        closeOnSubmit
      />
    </>
  )
}

// ─── Library content ──────────────────────────────────────────────────────────

const ICD10LibraryContent = () => {
  const { groups, handleAddGroup, handleGroupDragEnd, loading } = useICD10LibraryContext()
  const [addGroupOpen, setAddGroupOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [openGroupId, setOpenGroupId] = useState<string | null>(null)

  const openGroup = openGroupId ? (groups.find(g => g.id === openGroupId) ?? null) : null

  if (openGroup) {
    return <ICD10GroupDetailView group={openGroup} onBack={() => setOpenGroupId(null)} />
  }

  return (
    <>
      <TestFeatureModal isOpen={testOpen} onClose={() => setTestOpen(false)} />
      <ScrollableBodyContainer
        header={
          <Flex
            align="center"
            w="100%"
            bg="gray.50"
            _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
            borderBottomWidth={1}>
            <Text
              py={2}
              px={4}
              fontSize="xl"
              fontWeight={600}
              color="gray.700"
              _dark={{ color: 'gray.200' }}
              flex={1}>
              ICD-10 Code Library
            </Text>
            <ActionButton
              size="sm"
              mr={2}
              onClick={e => {
                e.stopPropagation()
                setTestOpen(true)
              }}>
              Test Feature
            </ActionButton>
            <Button
              size="sm"
              colorScheme="green"
              mr={2}
              isLoading={loading}
              onClick={e => {
                e.stopPropagation()
                setAddGroupOpen(true)
              }}>
              + Add Category
            </Button>
          </Flex>
        }
        flexProps={{ bg: 'gray.100', _dark: { bg: 'gray.700' } }}
        body={
          <DragDropContext onDragEnd={handleGroupDragEnd}>
            <Droppable droppableId="icd10-groups">
              {provided => (
                <VStack
                  p={2}
                  spacing={2}
                  w="100%"
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {groups.map((group, index) => (
                    <Draggable key={group.id} draggableId={group.id} index={index}>
                      {(draggableProvided, draggableSnapshot) => (
                        <Box
                          w="100%"
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          boxShadow={draggableSnapshot.isDragging ? 'lg' : undefined}>
                          <ICD10GroupCard
                            group={group}
                            groupDragHandleProps={draggableProvided.dragHandleProps}
                            onOpen={setOpenGroupId}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </VStack>
              )}
            </Droppable>
          </DragDropContext>
        }
      />

      <GenericEditModal<{ name: string }>
        field={{ name: 'Add Category', children: { name: groupNameTextField } }}
        isOpen={addGroupOpen}
        onClose={() => setAddGroupOpen(false)}
        onSubmit={(data, _onProgress) => handleAddGroup(data.name)}
        closeOnSubmit
      />
    </>
  )
}

// ─── Public export ────────────────────────────────────────────────────────────

export const ICD10LibraryAdminView = () => (
  <ICD10LibraryProvider>
    <ICD10LibraryContent />
  </ICD10LibraryProvider>
)
